import { FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import { CarrierType, EquipmentType } from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import { FieldBox, VesselIcon } from '@tom-ui/ui'
import { IFormStore, useRegisterSubFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { ICarrierVisitAssignment } from '../../models/operational-equipment-assignment.model'
import { CarrierVisitAssignmentSubFormStore } from '../../stores/carrier-visit-assignment.sub-form-store'
import { OperationalEquipmentAdd } from '../atoms/OperationalEquipmentAdd'
import { OperationalEquipmentAssignmentActions } from '../atoms/OperationalEquipmentAssignmentActions'
import { OperationalEquipmentDuplicatedAlert } from '../atoms/OperationalEquipmentDuplicatedAlert'
import { CarrierVisitAreaAutoComplete } from './CarrierVisitAreaAutoComplete'
import { OperationalEquipmentSharedAssignment } from './OperationalEquipmentSharedAssignment'

interface Props<T> {
  name: keyof T
  formStore: IFormStore<T>
  equipmentCount: number
  equipmentType?: EquipmentType
  initialSelectedAssignments: ICarrierVisitAssignment[]
}
export const OperationalEquipmentVesselAssignmentSubForm = observer(
  <T,>({
    name,
    formStore,
    equipmentCount,
    equipmentType,
    initialSelectedAssignments,
  }: Props<T>) => {
    const { t } = useTranslate()
    const store = useRegisterSubFormStore(formStore, name, new CarrierVisitAssignmentSubFormStore())

    useEffect(() => {
      store.setValues(initialSelectedAssignments, equipmentCount > 1)
    }, [initialSelectedAssignments, equipmentCount, store])

    return (
      <FieldBox
        label={t('vesselAssignment', 'Vessel assignment')}
        labelIcon={<VesselIcon />}
        rightRender={
          <OperationalEquipmentAdd
            canAdd={store.canAddAssignment}
            onAdd={() => store.addAssignment()}
            dataCy='add-vessel-assignment'
          />
        }
        displayChildren={store.displayContent}
      >
        <Grid container spacing={2} alignItems='center'>
          <OperationalEquipmentSharedAssignment
            equipmentCount={equipmentCount}
            equipmentType={equipmentType}
            assignmentsTotal={store.visitIds.value.length}
            canAddAssignment={!store.assignments.value.length}
            assignmentType={t('vesselVisits', 'Vessel visits')}
            onAdd={() => store.addAssignment()}
            onRemove={() => store.clearVisitIds()}
          />

          {store.assignments.value.map(
            (assignment: ICarrierVisitAssignment | null, index: number) =>
              assignment?.isDeleted !== true && (
                <Grid container size={12} key={`vessel-assignment-${index}`}>
                  <Grid size={5}>
                    <CarrierVisitAreaAutoComplete
                      dataCy={`vessel-assignment-${index}`}
                      label={t('vessel', 'Vessel')}
                      carrierType={CarrierType.Vessel}
                      selectedId={assignment?.visitId}
                      initialFilterName={assignment?.filter}
                      onChange={visit =>
                        store.updateAssignment(
                          index,
                          visit?.id,
                          null,
                          visit?.workAreas,
                          visit?.carrierName,
                        )
                      }
                      onLoadWorkAreas={workAreas =>
                        store.updateAssignment(
                          index,
                          assignment?.visitId,
                          assignment?.workAreaId,
                          workAreas,
                          assignment?.carrierName,
                        )
                      }
                    />
                  </Grid>

                  <Grid size={5}>
                    <FormControl fullWidth required={!!assignment?.visitId}>
                      <InputLabel>{t('crane', 'Crane')}</InputLabel>
                      <Select
                        label={t('crane', 'Crane')}
                        data-cy={`crane-assignment-${index}`}
                        variant='outlined'
                        required={!!assignment?.visitId}
                        error={
                          !!store.assignments.error &&
                          !!assignment?.visitId &&
                          !assignment.workAreaId
                        }
                        value={assignment?.workAreas.length === 0 ? '' : assignment?.workAreaId}
                        onChange={e =>
                          store.updateAssignment(
                            index,
                            assignment?.visitId,
                            e.target.value ? +e.target.value : undefined,
                            assignment?.workAreas,
                            assignment?.carrierName,
                          )
                        }
                      >
                        {assignment?.workAreas.map(workArea => (
                          <MenuItem
                            key={`crane-work-area-${workArea.id}`}
                            value={workArea.id}
                            data-cy={`crane-option-${workArea.craneName}`}
                          >
                            {workArea.craneName}
                          </MenuItem>
                        ))}
                        {assignment?.workAreas.length === 0 && (
                          <MenuItem disabled>{t('noOptions', 'No options')}</MenuItem>
                        )}
                      </Select>
                      {!!store.assignments.error &&
                        !!assignment?.visitId &&
                        !assignment.workAreaId && (
                          <FormHelperText error>
                            {t('fieldIsRequired', 'This field is required')}
                          </FormHelperText>
                        )}
                    </FormControl>
                  </Grid>

                  <OperationalEquipmentAssignmentActions
                    canAddAssignment={
                      index ===
                      store.assignments.value.reduce(
                        (lastIdx, a, i) => (a && a.isDeleted !== true ? i : lastIdx),
                        -1,
                      )
                    }
                    onAdd={() => store.addAssignment()}
                    onRemove={() => store.removeAssignment(index)}
                  />
                </Grid>
              ),
          )}
          <OperationalEquipmentDuplicatedAlert
            duplicatedAssignments={store.duplicatedAssignments}
            type='vessels'
          />
        </Grid>
      </FieldBox>
    )
  },
)
